import { useCallback, useEffect, useState } from "react";
import { History, FileDown, Pencil, Trash2, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { api, openSigned, formatApiError } from "@/lib/api";
import { nb, nok, formatDate, formatDateTime } from "@/lib/format";
import { SettlementsTab } from "./SettlementsTab";
import { AgreementsTab } from "./AgreementsTab";
import { EditShareholderDialog } from "./EditShareholderDialog";
import { NewShareholderDialog } from "./NewShareholderDialog";
import { NewPledgeDialog } from "./NewPledgeDialog";
import { NewDepotDialog } from "./NewDepotDialog";
import { EditTransactionDialog } from "./EditTransactionDialog";

function diffHoldings(before = {}, after = {}) {
  const keys = Array.from(new Set([...Object.keys(before || {}), ...Object.keys(after || {})]));
  return keys
    .map((k) => ({ k, b: before?.[k] || 0, a: after?.[k] || 0 }))
    .filter((d) => d.b !== d.a);
}

function txSummary(t = {}) {
  if (!t) return "";
  const parts = [t.type, t.share_class && `${nb(t.shares)} ${t.share_class}`, t.to_name && `til ${t.to_name}`, t.from_name && `fra ${t.from_name}`];
  return parts.filter(Boolean).join(" · ");
}

export function RegistryTabs({ rows, settlements, agreements = [], txs, classes, onChanged, companyId, canEdit, isAdmin, canManagePledge }) {
  const pendingCount = settlements.filter((s) => s.status === "venter_signering").length;
  const agreementPending = agreements.filter((a) => a.status === "venter_signering" || a.status === "venter_vedtak").length;
  const [log, setLog] = useState([]);
  const [pledges, setPledges] = useState([]);
  const [depots, setDepots] = useState([]);
  const [editTx, setEditTx] = useState(null);
  const [deleteTx, setDeleteTx] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteSh, setDeleteSh] = useState(null);
  const [deletingSh, setDeletingSh] = useState(false);
  const [deletePledge, setDeletePledge] = useState(null);
  const [deletingPledge, setDeletingPledge] = useState(false);
  const [deleteDepot, setDeleteDepot] = useState(null);
  const [deletingDepot, setDeletingDepot] = useState(false);

  const loadLog = useCallback(
    () => api.get(`/companies/${companyId}/share-log`).then((r) => setLog(r.data)).catch(() => setLog([])),
    [companyId]
  );
  const loadPledges = useCallback(
    () => api.get(`/companies/${companyId}/pledges`).then((r) => setPledges(r.data)).catch(() => setPledges([])),
    [companyId]
  );
  const loadDepots = useCallback(
    () => api.get(`/companies/${companyId}/depots`).then((r) => setDepots(r.data)).catch(() => setDepots([])),
    [companyId]
  );
  useEffect(() => { loadLog(); loadPledges(); loadDepots(); }, [loadLog, loadPledges, loadDepots]);

  const afterEdit = () => { onChanged?.(); loadLog(); };

  const openPledge = (p) => {
    openSigned(`/companies/${companyId}/pledges/${p.id}/agreement-link`).catch(() => toast.error("Kunne ikke åpne pantavtalen"));
  };

  const openDepot = (d) => {
    openSigned(`/companies/${companyId}/depots/${d.id}/agreement-link`).catch(() => toast.error("Kunne ikke åpne depoavtalen"));
  };

  const doDeletePledge = async () => {
    if (!deletePledge) return;
    setDeletingPledge(true);
    try {
      await api.delete(`/companies/${companyId}/pledges/${deletePledge.id}`);
      toast.success("Pant er slettet");
      setDeletePledge(null);
      loadPledges();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setDeletingPledge(false);
    }
  };

  const doDeleteDepot = async () => {
    if (!deleteDepot) return;
    setDeletingDepot(true);
    try {
      await api.delete(`/companies/${companyId}/depots/${deleteDepot.id}`);
      toast.success("Depo er slettet");
      setDeleteDepot(null);
      loadDepots();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setDeletingDepot(false);
    }
  };

  const doDelete = async () => {
    if (!deleteTx) return;
    setDeleting(true);
    try {
      await api.delete(`/admin/transactions/${deleteTx.id}`);
      toast.success("Transaksjonen er slettet og aksjeboka oppdatert");
      setDeleteTx(null);
      afterEdit();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setDeleting(false);
    }
  };

  const doDeleteSh = async () => {
    if (!deleteSh) return;
    setDeletingSh(true);
    try {
      await api.delete(`/admin/shareholders/${deleteSh.id}`);
      toast.success("Aksjonæren er slettet");
      setDeleteSh(null);
      afterEdit();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setDeletingSh(false);
    }
  };

  const openTx = (t) => {
    const link = t.agreement_id ? `/agreements/${t.agreement_id}/pdf-link` : t.settlement_id ? `/settlements/${t.settlement_id}/pdf-link` : null;
    if (!link) return;
    openSigned(link).catch(() => toast.error("Kunne ikke åpne avtalen"));
  };

  return (
    <Tabs defaultValue="eiere">
      <TabsList>
        <TabsTrigger value="eiere" data-testid="tab-eiere">Aksjeeiere</TabsTrigger>
        <TabsTrigger value="sluttsedler" data-testid="tab-sluttsedler">
          Sluttsedler{pendingCount ? <span className="ml-1.5 rounded-full bg-lime px-1.5 text-[10px] font-bold text-lime-foreground">{pendingCount}</span> : null}
        </TabsTrigger>
        <TabsTrigger value="avtaler" data-testid="tab-avtaler">
          Avtaler{agreementPending ? <span className="ml-1.5 rounded-full bg-lime px-1.5 text-[10px] font-bold text-lime-foreground">{agreementPending}</span> : null}
        </TabsTrigger>
        <TabsTrigger value="transaksjoner" data-testid="tab-transaksjoner">Transaksjoner</TabsTrigger>
        <TabsTrigger value="pant" data-testid="tab-pant">
          Pant{pledges.length ? <span className="ml-1.5 rounded-full bg-muted px-1.5 text-[10px] font-bold text-foreground">{pledges.length}</span> : null}
        </TabsTrigger>
        <TabsTrigger value="depo" data-testid="tab-depo">
          Depo{depots.length ? <span className="ml-1.5 rounded-full bg-muted px-1.5 text-[10px] font-bold text-foreground">{depots.length}</span> : null}
        </TabsTrigger>
        <TabsTrigger value="klasser" data-testid="tab-klasser">Klasser</TabsTrigger>
        <TabsTrigger value="endringslogg" data-testid="tab-endringslogg">Endringslogg</TabsTrigger>
      </TabsList>

      <TabsContent value="eiere" className="mt-4 rounded-xl border border-border/60 bg-card shadow-sm">
        {canEdit ? (
          <div className="flex items-center justify-between gap-2 border-b border-border/60 px-4 py-3">
            <span className="text-xs text-muted-foreground">Legg til en aksjonær manuelt i aksjeeierboka.</span>
            <NewShareholderDialog companyId={companyId} classes={classes} onSaved={afterEdit} />
          </div>
        ) : null}
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="text-xs uppercase tracking-wider">Eier</TableHead>
              <TableHead className="text-right text-xs uppercase tracking-wider">Aksjer</TableHead>
              <TableHead className="text-right text-xs uppercase tracking-wider">Eierandel</TableHead>
              <TableHead className="text-right text-xs uppercase tracking-wider">Stemmer</TableHead>
              {canEdit || isAdmin ? <TableHead className="w-20" /> : null}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.id} data-testid={`shareholder-${r.id}`}>
                <TableCell>
                  <p className="font-medium">{r.name}</p>
                  <p className="text-xs text-muted-foreground">{r.holder_type === "selskap" ? "Selskap" : "Person"} · {Object.entries(r.holdings).map(([k, v]) => `${nb(v)} ${k}`).join(", ")}</p>
                </TableCell>
                <TableCell className="text-right font-medium">{nb(r.shares)}</TableCell>
                <TableCell className="text-right"><span className="inline-flex items-center gap-1 font-semibold text-primary">{r.ownership_pct}%</span></TableCell>
                <TableCell className="text-right text-muted-foreground">{r.votes_pct}%</TableCell>
                {canEdit || isAdmin ? (
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      {canEdit ? <EditShareholderDialog companyId={companyId} shareholder={r} classes={classes} onSaved={afterEdit} /> : null}
                      {isAdmin ? (
                        <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive hover:text-destructive" data-testid={`shareholder-delete-${r.id}`} title="Slett aksjonær" onClick={() => setDeleteSh(r)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      ) : null}
                    </div>
                  </TableCell>
                ) : null}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TabsContent>

      <TabsContent value="sluttsedler" className="mt-4 rounded-xl border border-border/60 bg-card shadow-sm">
        <SettlementsTab settlements={settlements} onChanged={onChanged} />
      </TabsContent>

      <TabsContent value="avtaler" className="mt-4 rounded-xl border border-border/60 bg-card shadow-sm">
        <AgreementsTab agreements={agreements} onChanged={onChanged} />
      </TabsContent>

      <TabsContent value="transaksjoner" className="mt-4 rounded-xl border border-border/60 bg-card shadow-sm">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="text-xs uppercase tracking-wider">Dato</TableHead>
              <TableHead className="text-xs uppercase tracking-wider">Type</TableHead>
              <TableHead className="text-xs uppercase tracking-wider">Detaljer</TableHead>
              <TableHead className="text-right text-xs uppercase tracking-wider">Aksjer</TableHead>
              {isAdmin ? <TableHead className="w-20 text-right text-xs uppercase tracking-wider">Rett</TableHead> : null}
            </TableRow>
          </TableHeader>
          <TableBody>
            {txs.map((t) => {
              const linked = !!(t.agreement_id || t.settlement_id);
              return (
                <TableRow
                  key={t.id}
                  data-testid={`tx-row-${t.id}`}
                  onClick={() => linked && openTx(t)}
                  className={linked ? "cursor-pointer transition-colors hover:bg-muted/50" : ""}
                  title={linked ? "Åpne signert avtale (PDF)" : undefined}
                >
                  <TableCell className="whitespace-nowrap text-sm">{formatDate(t.date)}</TableCell>
                  <TableCell><span className="rounded-full bg-muted px-2 py-0.5 text-xs">{t.type}</span></TableCell>
                  <TableCell className="text-sm">
                    <p className="flex items-center gap-1.5">{t.to_name}{linked ? <FileDown className="h-3.5 w-3.5 text-primary" /> : null}</p>
                    <p className="text-xs text-muted-foreground">{t.share_class}{t.from_name ? ` · fra ${t.from_name}` : ""}{t.price_per_share ? ` · ${nok(t.price_per_share)}/aksje` : ""}</p>
                  </TableCell>
                  <TableCell className="text-right font-medium">{nb(t.shares)}</TableCell>
                  {isAdmin ? (
                    <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                      <div className="flex justify-end gap-1">
                        <Button size="icon" variant="ghost" className="h-7 w-7" data-testid={`tx-edit-${t.id}`} title="Rett transaksjon" onClick={() => setEditTx(t)}>
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive hover:text-destructive" data-testid={`tx-delete-${t.id}`} title="Slett transaksjon" onClick={() => setDeleteTx(t)}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </TableCell>
                  ) : null}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TabsContent>

      <TabsContent value="pant" className="mt-4 rounded-xl border border-border/60 bg-card shadow-sm">
        {canManagePledge ? (
          <div className="flex items-center justify-between gap-2 border-b border-border/60 px-4 py-3">
            <span className="text-xs text-muted-foreground">Registrer pant i aksjer og last opp pantavtalen.</span>
            <NewPledgeDialog companyId={companyId} rows={rows} classes={classes} onSaved={loadPledges} />
          </div>
        ) : null}
        {pledges.length === 0 ? (
          <div className="flex flex-col items-center gap-2 p-10 text-center text-sm text-muted-foreground" data-testid="pledges-empty">
            <ShieldCheck className="h-8 w-8 text-muted-foreground/50" />
            Ingen pant er registrert i aksjene ennå.
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-xs uppercase tracking-wider">Aksjonær</TableHead>
                <TableHead className="text-right text-xs uppercase tracking-wider">Aksjer</TableHead>
                <TableHead className="text-xs uppercase tracking-wider">Panthaver</TableHead>
                <TableHead className="text-xs uppercase tracking-wider">Avtale</TableHead>
                <TableHead className="text-xs uppercase tracking-wider">Dato</TableHead>
                {canManagePledge ? <TableHead className="w-16" /> : null}
              </TableRow>
            </TableHeader>
            <TableBody>
              {pledges.map((p) => (
                <TableRow key={p.id} data-testid={`pledge-row-${p.id}`}>
                  <TableCell className="font-medium">{p.shareholder_name}</TableCell>
                  <TableCell className="text-right font-medium">{nb(p.shares)}{p.share_class ? <span className="ml-1 text-xs text-muted-foreground">{p.share_class}</span> : null}</TableCell>
                  <TableCell>
                    {(p.pledgees || []).map((pl, i) => (
                      <div key={i} className={i > 0 ? "mt-1.5 border-t border-border/40 pt-1.5" : ""}>
                        <p className="text-sm">{pl.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {pl.kind === "selskap" ? "Selskap" : "Person"}
                          {pl.kind === "selskap" && pl.contact_name ? ` · Kontakt: ${pl.contact_name}` : ""}
                        </p>
                      </div>
                    ))}
                  </TableCell>
                  <TableCell>
                    {p.has_agreement ? (
                      <Button variant="ghost" size="sm" className="h-8 gap-1.5 px-2 text-primary" data-testid={`pledge-agreement-${p.id}`} onClick={() => openPledge(p)}>
                        <FileDown className="h-3.5 w-3.5" /> Åpne
                      </Button>
                    ) : <span className="text-xs text-muted-foreground">—</span>}
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-sm text-muted-foreground">{p.date ? formatDate(p.date) : "—"}</TableCell>
                  {canManagePledge ? (
                    <TableCell className="text-right">
                      <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive hover:text-destructive" data-testid={`pledge-delete-${p.id}`} title="Slett pant" onClick={() => setDeletePledge(p)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  ) : null}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </TabsContent>

      <TabsContent value="depo" className="mt-4 rounded-xl border border-border/60 bg-card shadow-sm">
        {canManagePledge ? (
          <div className="flex items-center justify-between gap-2 border-b border-border/60 px-4 py-3">
            <span className="text-xs text-muted-foreground">Registrer depo på aksjer med valgfri avtale og kommentar.</span>
            <NewDepotDialog companyId={companyId} rows={rows} classes={classes} onSaved={loadDepots} />
          </div>
        ) : null}
        {depots.length === 0 ? (
          <div className="flex flex-col items-center gap-2 p-10 text-center text-sm text-muted-foreground" data-testid="depots-empty">
            <ShieldCheck className="h-8 w-8 text-muted-foreground/50" />
            Ingen depo er registrert på aksjene ennå.
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-xs uppercase tracking-wider">Aksjonær</TableHead>
                <TableHead className="text-right text-xs uppercase tracking-wider">Aksjer</TableHead>
                <TableHead className="text-xs uppercase tracking-wider">Depomottaker</TableHead>
                <TableHead className="text-xs uppercase tracking-wider">Kommentar</TableHead>
                <TableHead className="text-xs uppercase tracking-wider">Avtale</TableHead>
                <TableHead className="text-xs uppercase tracking-wider">Dato</TableHead>
                {canManagePledge ? <TableHead className="w-16" /> : null}
              </TableRow>
            </TableHeader>
            <TableBody>
              {depots.map((d) => (
                <TableRow key={d.id} data-testid={`depot-row-${d.id}`}>
                  <TableCell className="font-medium">{d.shareholder_name}</TableCell>
                  <TableCell className="text-right font-medium">{nb(d.shares)}{d.share_class ? <span className="ml-1 text-xs text-muted-foreground">{d.share_class}</span> : null}</TableCell>
                  <TableCell>
                    {(d.holders || []).map((h, i) => (
                      <div key={i} className={i > 0 ? "mt-1.5 border-t border-border/40 pt-1.5" : ""}>
                        <p className="text-sm">{h.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {h.kind === "selskap" ? "Selskap" : "Person"}
                          {h.kind === "selskap" && h.contact_name ? ` · Kontakt: ${h.contact_name}` : ""}
                        </p>
                      </div>
                    ))}
                  </TableCell>
                  <TableCell className="max-w-[200px] text-sm text-muted-foreground">{d.note || "—"}</TableCell>
                  <TableCell>
                    {d.has_agreement ? (
                      <Button variant="ghost" size="sm" className="h-8 gap-1.5 px-2 text-primary" data-testid={`depot-agreement-${d.id}`} onClick={() => openDepot(d)}>
                        <FileDown className="h-3.5 w-3.5" /> Åpne
                      </Button>
                    ) : <span className="text-xs text-muted-foreground">—</span>}
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-sm text-muted-foreground">{d.date ? formatDate(d.date) : "—"}</TableCell>
                  {canManagePledge ? (
                    <TableCell className="text-right">
                      <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive hover:text-destructive" data-testid={`depot-delete-${d.id}`} title="Slett depo" onClick={() => setDeleteDepot(d)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  ) : null}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </TabsContent>

      <TabsContent value="klasser" className="mt-4 rounded-xl border border-border/60 bg-card shadow-sm">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="text-xs uppercase tracking-wider">Klasse</TableHead>
              <TableHead className="text-xs uppercase tracking-wider">Stemmer/aksje</TableHead>
              <TableHead className="text-xs uppercase tracking-wider">Merknad</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {classes.map((c) => (
              <TableRow key={c.id}>
                <TableCell className="font-medium">{c.name}</TableCell>
                <TableCell>{c.votes_per_share}</TableCell>
                <TableCell className="text-sm text-muted-foreground">{c.note}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TabsContent>

      <TabsContent value="endringslogg" className="mt-4 rounded-xl border border-border/60 bg-card shadow-sm">
        {log.length === 0 ? (
          <div className="flex flex-col items-center gap-2 p-10 text-center text-sm text-muted-foreground" data-testid="share-log-empty">
            <History className="h-8 w-8 text-muted-foreground/50" />
            Ingen manuelle aksjejusteringer er logget ennå.
          </div>
        ) : (
          <div className="divide-y divide-border/60" data-testid="share-log-list">
            {log.map((l) => (
              <div key={l.id} className="flex items-start gap-3 px-5 py-3.5" data-testid={`share-log-${l.id}`}>
                <div className="mt-0.5 grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-primary/10 text-primary">
                  <History className="h-4 w-4" />
                </div>
                <div className="min-w-0 flex-1">
                  {l.kind === "transaction" ? (
                    <>
                      <p className="text-sm">
                        <span className="font-semibold">{l.changed_by_name}</span> {l.action === "delete" ? "slettet en transaksjon" : "rettet en transaksjon"}
                      </p>
                      <div className="mt-1 space-y-0.5 text-[11px] text-muted-foreground">
                        <p><span className="font-medium text-foreground">Før:</span> {txSummary(l.before)}</p>
                        {l.action !== "delete" && l.after ? <p><span className="font-medium text-foreground">Etter:</span> {txSummary(l.after)}</p> : null}
                      </div>
                    </>
                  ) : l.kind === "shareholder" ? (
                    <>
                      <p className="text-sm">
                        <span className="font-semibold">{l.changed_by_name}</span> slettet aksjonær <span className="font-semibold">{l.shareholder_name}</span>
                      </p>
                      {Object.keys(l.before || {}).length ? (
                        <div className="mt-1 flex flex-wrap gap-1.5">
                          {Object.entries(l.before).map(([k, v]) => (
                            <span key={k} className="rounded-full bg-muted px-2 py-0.5 text-[11px] text-muted-foreground">{k}: {nb(v)} → <span className="font-semibold text-foreground">0</span></span>
                          ))}
                        </div>
                      ) : null}
                    </>
                  ) : (
                    <>
                      <p className="text-sm">
                        <span className="font-semibold">{l.changed_by_name}</span> justerte aksjer for <span className="font-semibold">{l.shareholder_name}</span>
                      </p>
                      <div className="mt-1 flex flex-wrap gap-1.5">
                        {diffHoldings(l.before, l.after).map((d) => (
                          <span key={d.k} className="rounded-full bg-muted px-2 py-0.5 text-[11px] text-muted-foreground">
                            {d.k}: {nb(d.b)} → <span className="font-semibold text-foreground">{nb(d.a)}</span>
                          </span>
                        ))}
                      </div>
                    </>
                  )}
                </div>
                <span className="shrink-0 whitespace-nowrap text-xs text-muted-foreground">{formatDateTime(l.at)}</span>
              </div>
            ))}
          </div>
        )}
      </TabsContent>

      {editTx ? (
        <EditTransactionDialog tx={editTx} rows={rows} classes={classes} open={!!editTx} onOpenChange={(o) => { if (!o) setEditTx(null); }} onSaved={afterEdit} />
      ) : null}
      <AlertDialog open={!!deleteTx} onOpenChange={(o) => { if (!o) setDeleteTx(null); }}>
        <AlertDialogContent data-testid="delete-tx-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Slette transaksjon?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTx ? `«${txSummary(deleteTx)}» fjernes, og aksjebeholdningen korrigeres tilbake. Handlingen logges i Endringslogg.` : ""}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Avbryt</AlertDialogCancel>
            <AlertDialogAction disabled={deleting} onClick={(e) => { e.preventDefault(); doDelete(); }} data-testid="confirm-delete-tx" className="gap-2">
              {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />} Slett
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!deleteSh} onOpenChange={(o) => { if (!o) setDeleteSh(null); }}>
        <AlertDialogContent data-testid="delete-shareholder-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Slette aksjonær?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteSh ? (
                deleteSh.shares > 0
                  ? `«${deleteSh.name}» eier ${nb(deleteSh.shares)} aksjer. Sletting fjerner aksjonæren OG aksjene fra aksjeboka. Er du sikker?`
                  : `«${deleteSh.name}» fjernes fra aksjeeierboka. Handlingen logges i Endringslogg.`
              ) : ""}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Avbryt</AlertDialogCancel>
            <AlertDialogAction disabled={deletingSh} onClick={(e) => { e.preventDefault(); doDeleteSh(); }} data-testid="confirm-delete-shareholder" className="gap-2">
              {deletingSh ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />} Slett
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog open={!!deletePledge} onOpenChange={(o) => { if (!o) setDeletePledge(null); }}>
        <AlertDialogContent data-testid="delete-pledge-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Slette pant?</AlertDialogTitle>
            <AlertDialogDescription>
              {deletePledge ? `Pant på ${nb(deletePledge.shares)} aksjer for «${deletePledge.shareholder_name}» (panthaver ${deletePledge.pledgee_name}) fjernes. Handlingen kan ikke angres.` : ""}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Avbryt</AlertDialogCancel>
            <AlertDialogAction disabled={deletingPledge} onClick={(e) => { e.preventDefault(); doDeletePledge(); }} data-testid="confirm-delete-pledge" className="gap-2">
              {deletingPledge ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />} Slett
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog open={!!deleteDepot} onOpenChange={(o) => { if (!o) setDeleteDepot(null); }}>
        <AlertDialogContent data-testid="delete-depot-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Slette depo?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteDepot ? `Depo på ${nb(deleteDepot.shares)} aksjer for «${deleteDepot.shareholder_name}» (depomottaker ${deleteDepot.holder_name}) fjernes. Handlingen kan ikke angres.` : ""}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Avbryt</AlertDialogCancel>
            <AlertDialogAction disabled={deletingDepot} onClick={(e) => { e.preventDefault(); doDeleteDepot(); }} data-testid="confirm-delete-depot" className="gap-2">
              {deletingDepot ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />} Slett
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Tabs>
  );
}
